// Copyright 2022, Imprivata, Inc.  All rights reserved.

import type { ApiErrorPayloadCommon } from '@imprivata-cloud/common';
import type { CodingContext, Secret } from '@imprivata-cloud/data-privacy-js';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { usePlatform } from '../../context/platform/hooks';
import type { FhirPatient } from '../../models/fhir-patient';
import { apiClient } from '../client';

const getFhirPatientQueryKey = 'get-fhir-patient';

interface GetPatientResponse {
  id: string;
  givenName: Secret;
  surname: Secret;
  dateOfBirth: Secret;
  mrn: Secret;
  isAboveMinimumAge: boolean;
}

const getFhirPatientQuery = async (codingContext: CodingContext | undefined): Promise<FhirPatient> => {
  const response = await apiClient.get<GetPatientResponse>('fhir/patient', codingContext);
  return {
    id: response.id,
    givenName: codingContext?.decryptToString(response.givenName) ?? '',
    surname: codingContext?.decryptToString(response.surname) ?? '',
    dateOfBirth: codingContext?.decryptToString(response.dateOfBirth) ?? '',
    mrn: codingContext?.decryptToString(response.mrn) ?? '',
    isAboveMinimumAge: response.isAboveMinimumAge,
  };
};

export const useGetFhirPatientQuery = (isEnabled = true) => {
  const { codingContext } = usePlatform();
  const [enabled, setEnabled] = useState(isEnabled);
  const query = useQuery<FhirPatient, ApiErrorPayloadCommon>({
    queryKey: [getFhirPatientQueryKey],
    queryFn: () => getFhirPatientQuery(codingContext),
    enabled: enabled && !!codingContext,
    retry: (failureCount, apiError) => {
      const errorCode = apiError?.error?.code;
      if (
        errorCode === 'minimum-age' ||
        errorCode === 'missing-info' ||
        errorCode === 'session-not-found' ||
        errorCode === 'unauthorized'
      ) {
        return false;
      }
      return failureCount < 3;
    },
    refetchOnWindowFocus: false,
  });

  return {
    patient: query.data,
    isLoading: query.isLoading,
    error: query.error,
    enable: () => setEnabled(true),
  };
};
